<template>
	<t-input-wrapper
		:label="label"
		:description="description"
		:hint="hint"
		:isInvalid="isInvalid"
		:validationMessage="validationMessage"
		:warningMessage="warningMessage"
		class="number-label"
	>
		<input
			:style="getStyle"
			:value="value"
			:class="[{invalid: isInvalid, disabled: disabled, showwheel: !showWheel, leftAlign: leftAlign}, inputCssClass, size]"
			:placeholder="placeholder"
			:disabled="disabled"
			:tabindex="disableTabbing ? -1 : 0"
			:label="label"
			:min="min"
			:max="max"
			type="number"
			class="input"
			v-on="inputListeners"
		>
		<span
			v-if="extraDisplayText && value"
			:style="displayLeft"
			:class="[{leftAlign: leftAlign}]"
			class="extra-display-text"
		>{{extraDisplayText}}</span>
	</t-input-wrapper>
</template>

<script>
import formMixin from '../../inputs/formMixin';
import inputWrapperMixin from '../../inputs/inputWrapperMixin';
import TInputWrapper from '../../inputs/TInputWrapper';

// There is a bug where extraDisplayText gets misplaced if a label is also supplied to the component.
// extraDisplayText does neither work with right-adjusted text.
export default {
	name: 'TNumberInput',
	components: { TInputWrapper },
	mixins: [ formMixin, inputWrapperMixin ],
	props: {
		inputCssClass: String,
		min: String,
		max: String,
		extraDisplayText: String,
		hideZeroValue: {
			type: Boolean,
			default: false
		},
		showWheel: {
			type: Boolean,
			default: false
		},
		disableTabbing: {
			type: Boolean,
			default: false
		},
		leftAlign: {
			type: Boolean,
			default: false
		},
		castToNumber: Boolean,
	},

	computed: {
		displayLeft() {
			let numDigits = 0;
			let { value } = this;
			if (this.hideZeroValue && parseInt(value) === 0) {
				return 'left: 10px;';
			} else {
				while (Math.abs(value) >= 1) {
					numDigits++;
					value = value/10;
				}
				return `left: calc(20px + ${numDigits}ch);`;
			}
		},
		getStyle() {
			let obj = {
				width: this.width,
				paddingLeft: this.paddingLeft
			};
			if (this.hideZeroValue && parseInt(this.value) === 0 && this.extraDisplayText) {
				obj.color = 'transparent';
			}
			return obj;
		},

		inputListeners() {
			return {
				...this.$listeners,
				input: event => {
					let value = this.castToNumber ? Number(event.target.value) : event.target.value;
					this.$emit('input', value);
				},
				change: event => {
					let value = this.castToNumber ? Number(event.target.value) : event.target.value;
					this.$emit('change', value);
				}
			};
		},
	},
};
</script>

<style lang="scss" scoped>
@import '../../inputs/scss/form';
.number-label{
	position:relative;
	.extra-display-text {
		pointer-events: none;
		position: absolute;
		line-height: 32px;
	}
}
.input {
	text-align: right;
}
.leftAlign {
	text-align: left
}
</style>


<docs>
## Examples

### Simple Text input

```jsx
<div style="display: inline-grid; grid-row-gap: 10px; grid-template-rows: repeat(3, auto); width: 100%;">
	<t-number-input width="280px" placeholder="Number input placeholder..."/>
	<t-number-input width="280px" value="2" disabled  extraDisplayText="Extra display text"/>
	<t-number-input width="280px" value="1" isInvalid />
</div>
```

### Label and description

```jsx
<t-number-input
	width="280px"
	label="Label"
	description="This is a description."
	placeholder="Placeholder text"
/>
```

### Hint

```jsx
<t-number-input
	width="280px"
	label="Label"
	hint="This is a hint"
	placeholder="Placeholder text..."
/>
```

### Validation message

```jsx
<t-number-input
	width="280px"
	label="Label"
	placeholder="Placeholder text..."
	validationMessage="This is a validation message"
	isInvalid
/>
```
</docs>
